import { useRef } from 'react';
import { difference } from 'lodash';

import { syncedMeasurements } from '../../components/measurementsStore/elementMeasurements/syncedMeasurementsSingleton';

import { ElementMeasurement } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';

export type RenderListPlaceholdersReturnProps = {
    allowElementPlaceholder: boolean;
    saveElementRenderState: () => void;
    clearPreviousElementRenderState: () => void;
    onElementVisibilityChange: (elementId: string, isVisible: boolean) => void;
    elementPlaceholderMeasurements: { [a: string]: ElementMeasurement } | null;
    getInitialRenderSize: (defaultRenderSize: number, childIds: string[]) => number;
    getInitialElementsVisibility: (childIds: string[], defaultVisibleCount?: number) => Map<string, boolean>;
};

/**
 * Adds list placeholder functionality to a component.
 *
 * Keeps track of the element measurements in the list so that placeholders can be
 * used on following renders, and calculates the initial render size based on how many
 * placeholders can be rendered.
 *
 * If retainPreviousVisibleElements is true, it will also keep track of the visibility of elements
 * on close so that the same ones will be visible when the list is opened again.
 */
const useRenderListPlaceholders = (retainPreviousVisibleElements?: boolean): RenderListPlaceholdersReturnProps => {
    const currentElementVisibilityRef = useRef(new Map());
    const prevElementsVisibilityRef = useRef(new Map());
    const elementPlaceholderMeasurementsRef = useRef<{ [a: string]: ElementMeasurement } | null>(null);

    /**
     * Save current measurements for the next time inbox is opened
     * If retainPreviousVisibleElements is true, also save the current visibility of elements
     */
    const saveElementRenderState = () => {
        elementPlaceholderMeasurementsRef.current = { ...syncedMeasurements };
        if (!retainPreviousVisibleElements) return;

        prevElementsVisibilityRef.current = currentElementVisibilityRef.current;
    };

    /**
     * Clear the previous element render state
     * Run this when we no longer need the previous measurements and visibility,
     * e.g. for unsorted notes when changing boards
     */
    const clearPreviousElementRenderState = () => {
        elementPlaceholderMeasurementsRef.current = null;
        currentElementVisibilityRef.current.clear();
        prevElementsVisibilityRef.current.clear();
    };

    const onElementVisibilityChange = (elementId: string, isVisible: boolean) => {
        if (!retainPreviousVisibleElements) return;

        currentElementVisibilityRef.current.set(elementId, isVisible);
    };

    /**
     * Check if we should add previously loaded elements to the initial render count, so we can continue the batched
     * render instead of restarting, since we likely have the measurements to load placeholders for most of them
     * It seems to work best if we just use this for lists where we retain previous visible elements
     */
    const getInitialRenderSize = (defaultRenderSize: number, childIds: string[]) => {
        if (!elementPlaceholderMeasurementsRef.current) return defaultRenderSize;

        const preMeasuredElements = childIds?.filter(
            (elementId) => elementPlaceholderMeasurementsRef.current?.[elementId],
        );
        const preMeasuredElementsCount = preMeasuredElements.length || 0;
        if (preMeasuredElementsCount === 0) return defaultRenderSize;

        const potentialFirstBatch = childIds.slice(0, preMeasuredElementsCount);
        const missingMeasurements = difference(potentialFirstBatch, preMeasuredElements);

        // Only use this number if there are enough measurements to render placeholders for most elements.
        // There may be some missing if new elements have been added since last render
        if (missingMeasurements.length < defaultRenderSize) {
            return preMeasuredElementsCount;
        }

        return defaultRenderSize;
    };

    /**
     * Return the initial visibility of elements based on the previous state
     * This value can be used to determine whether to show the placeholder
     * before the ElementVisibilityObserver has rendered
     */
    const getInitialElementsVisibility = (childIds: string[], defaultVisibleCount = 10) => {
        if (retainPreviousVisibleElements && prevElementsVisibilityRef.current) {
            return prevElementsVisibilityRef.current;
        }

        const initialElementsVisibility = new Map();
        childIds.forEach((elementId, index) => {
            initialElementsVisibility.set(elementId, index < defaultVisibleCount);
        });

        return initialElementsVisibility;
    };

    return {
        allowElementPlaceholder: true,
        saveElementRenderState,
        clearPreviousElementRenderState,
        onElementVisibilityChange,
        elementPlaceholderMeasurements: elementPlaceholderMeasurementsRef.current,
        getInitialRenderSize,
        getInitialElementsVisibility,
    };
};

export default useRenderListPlaceholders;
